import "./all_case_view.css";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Body from "../Navigation/Body";
import CaseTable from "./CaseTable";
import Moment from "react-moment";
import Nav from "react-bootstrap/Nav";
import { NavLink, useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { patientActions } from "../../actions";
import { useApi } from "../../contexts/ApiProvider";
import { useSurveyMode } from "../../contexts/SurveyModeProvider";
import ProgressBar from "react-bootstrap/ProgressBar";
import Badge from "react-bootstrap/Badge";
import Button from 'react-bootstrap/Button';
import { surveyActions } from "../../actions";
import { useUser } from "../../contexts/UserProvider";
import { Modal } from "react-bootstrap";
import hpruLogo from "../../imgs/HPRU HCAI AMR logo.png";
import oxLogo from "../../imgs/ox-logo.png";
import bdiLogo from "../../imgs/bdi_logo.jpg";
import mmmLogo from "../../imgs/mmm_logo.png";

export default function ExplorePage() {
  const api = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { debug_mode } = useSurveyMode();
  const { user, save, isAdmin } = useUser();

  const survey_history = useSelector((state) => state.survey.surveyHistory);
  console.log("survey_history", survey_history);

  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [responsesList, setResponsesList] = useState([]);

  //reset tree before new api call
  dispatch(patientActions.storeTree({ tree: undefined }, {}));
  dispatch(patientActions.storeSnpDistance(5));

  const surveyed = Object.keys(survey_history).filter(
    (key) => survey_history[key].mode >= 4
  );

  function surv_classifier(d) {
    if (d.hour_diff > 48) return `HO-HA`;
    else if (d.admission_id_4w) return `CO-HA`;
    if (d.admission_id_12w) return "CO-Indeterminate";
    else return "CO-CA";
  }

  const disable = (micro_id) => {
    return surveyed.includes(String(micro_id));
  };

    // Handle clicking on a case (row or button)
    const handleDebugClick = (row) => {
      dispatch(
        surveyActions.storeActiveCase({
          case_id: row.case_id,
          patient_mrn: row.mrn,
          patient_name: `${row.firstname} ${row.surname}`,
          micro_id: row.micro_id,
          start_timestamp: Date.now(),
        })
      );
      setTimeout(async () => {
        await save();
      }, 0);
      navigate(`/patient/${row.micro_id}`);
    };
  
    const handleSurveyClick = (row) => {
      const existing = survey_history[row.micro_id];
      const mode = existing?.mode ?? 0;
  
      if (!existing) {
        dispatch(
          surveyActions.storeSurveyedObj(
            row.micro_id,
            row.case_id,
            row.mrn,
            `${row.firstname} ${row.surname}`,
            1 // Start at mode = 1
          )
        );
        setTimeout(async () => {
          await save();
        }, 0);
      }
      
      if (mode >= 4) return; // Do nothing if already completed
  
      dispatch(
        surveyActions.storeActiveCase({
          case_id: row.case_id,
          patient_mrn: row.mrn,
          patient_name: `${row.firstname} ${row.surname}`,
          micro_id: row.micro_id,
          start_timestamp: Date.now(),
        })
      );
      navigate(`/patient/${row.micro_id}`);
    };

    const handleRowClick = (row) => {
      debug_mode ? handleDebugClick(row) : handleSurveyClick(row);
    };

  // Debug Mode
  const debugColumns = useMemo(
    () => [
      { Header: "Name", accessor: (d) => `${d.firstname} ${d.surname}` },
      { Header: "MRN", accessor: "mrn" },
      { Header: "Category", 
        accessor: (d) => `${d.collapsed_case_category} (${d.case_category})`,
        Cell: ({ value }) => (
          <Badge bg="" style={{ fontSize: "0.9rem", pointerEvents: "none", backgroundColor: "#E67E22" }}>
            {value}
          </Badge>
        ),},
      {
        Header: "Collection date",
        accessor: "collectdt",
        Cell: (e) => <Moment format="DD-MMM-YYYY">{e.value}</Moment>,
      },
      {
        Header: "Test location",
        accessor: "admission_id",
        Cell: (props) => {
          return props.value === null ? "Community" : "Hospital";
        },
      },
      {
        Header: "Onset",
        accessor: "hour_diff",
        Cell: (props) => {
          return props.value < 48
            ? `Community onset`
            : `Hospital onset (${props.value}h)`;
        },
      },
      { Header: "Classification", accessor: (d) => surv_classifier(d) },
      {
        Header: "View sources",
        accessor: "micro_id",
        Cell: ({ row }) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleDebugClick(row.original);
            }}
            className="btn btn-primary"
            size="sm"
          >
            Patient Summary
          </Button>
        ),
      },
    ],
    [navigate]
  );

  // Survey Mode
  const surveyColumns = useMemo(
    () => [
      {
        Header: "Case",
        accessor: "shuffled_case_number",
        Cell: ({ value }) => (
          <Badge pill bg="" style={{ fontSize: "0.9rem", pointerEvents: "none", backgroundColor: "#2C3E50" }}>
            {value}
          </Badge>
        ),
      },
      ...(isAdmin
        ? [{
          Header: "Case ID",
          accessor: "case_id",
          // Changed: Display as a chip using <Badge>
          Cell: ({ value }) => (
            <Badge pill bg="" style={{ fontSize: "0.9rem", pointerEvents: "none", backgroundColor: "#1ABC9C" }}>
              {value}
            </Badge>
          ),
        }]
      : []),
      {
        Header: "MRN",
        accessor: "mrn",
      },
      { Header: "Name", accessor: (d) => `${d.firstname} ${d.surname}` },
      {
        Header: "Positive Test",
        accessor: "pathogen_name",
        style: { width: "1%" }, // minimise width
        Cell: () => (
          <Badge pill bg="" style={{ fontSize: "0.9rem", pointerEvents: "none", backgroundColor: "#E74C3C"  }}>
            <i>Clostridiodes difficile</i> Toxin
          </Badge>
        ),
      },
      ...(isAdmin
        ? [{
            Header: "Category",
            accessor: d => `${d.collapsed_case_category} (${d.case_category})`,
            Cell: ({ value }) => (
              <Badge bg="" style={{ fontSize: "0.9rem", pointerEvents: "none", backgroundColor: "#E67E22" }}>
                {value}
              </Badge>
            ),
          }]
        : []),
      {
        Header: "Test Date",
        accessor: "collectdt",
        Cell: (e) => <Moment format="DD-MMM-YYYY">{e.value}</Moment>,
      },
      {
        Header: "Progress",
        accessor: "progress",
        style: { minWidth: "250px" },
        Cell: ({ row }) => {
          const existing = survey_history[row.original.micro_id];
          const mode = existing?.mode ?? 0;
        
          // Colors used
          const completed_color = "#06d6a0";
          const not_started_color = "lightgray";
        
          // Striped pattern for the "in progress" step
          const in_progress_striped = `repeating-linear-gradient(
            305deg,
            #ffd166 0,
            #ffd166 6px,
            #ffb703 6px,
            #ffb703 10px
          )`;
        
          // Decide label on top of the bars
          let topLabel = "";
          if (mode === 0) {
            topLabel = "Not Started";
          } else if (mode >= 4) {
            topLabel = "Completed";
          }
        
          function getStepBackground(stepNumber) {
            // If not started => all bars gray
            if (mode === 0) return not_started_color;
            // If completed => all bars green
            if (mode >= 4) return completed_color;
            // Otherwise, check the current phase
            if (mode > stepNumber) {
              return completed_color;
            } else if (mode === stepNumber) {
              return in_progress_striped;
            } else {
              return not_started_color;
            }
          }
        
          // Step backgrounds
          const step1Background = getStepBackground(1);
          const step2Background = getStepBackground(2);
          const step3Background = getStepBackground(3);
        
          // Render the top label + the 3 bars
          return (
            <div>
              <div style={{ display: "flex", gap: "8px", marginTop: "2px" }}>
                {/* Step 1 */}
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div
                    style={{
                      background: step1Background,
                      color: "#000",
                      height: "10px",
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                  {!topLabel && mode === 1 && (
                    <div style={{ marginLeft: "2px", marginTop: "2px", marginBottom: "-4px", fontSize: "0.8rem" }}>Phase 1</div>
                  )}
                </div>
        
                {/* Step 2 */}
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div
                    style={{
                      background: step2Background,
                      color: "#000",
                      height: "10px",
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                {!topLabel && mode === 2 && (
                  <div style={{ marginLeft: "2px", marginTop: "2px", marginBottom: "-4px",fontSize: "0.8rem" }}>Phase 2</div>
                )}
                </div>
        
                {/* Step 3 */}
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div
                    style={{
                      background: step3Background,
                      color: "#000",
                      height: "10px",
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                {!topLabel && mode === 3 && (
                  <div style={{ marginLeft: "2px", marginTop: "2px", marginBottom: "-4px", fontSize: "0.8rem" }}>Phase 3</div>
                )}
                </div>
              </div>
              {/* If topLabel is not empty, show it above */}
              {topLabel && (
                <div style={{ marginLeft: "2px", marginTop: "2px", marginBottom: "-4px", fontSize: "0.8rem" }}>
                  {topLabel}
                </div>
              )}
        
            </div>
          );
        },        
      },
      {
        Header: "",
        accessor: "micro_id",
        style: { width: "10%" },
        Cell: ({ row }) => {
          // 'row.original' has all row data, e.g. row.original.case_id, row.original.patient_id
          const existing = survey_history[row.original.micro_id];
          const mode = existing?.mode ?? 0;
          const isCompleted = mode >= 4;

          // Decide button label:
          let buttonLabel = "Start Case ▶";
          if (isCompleted) {
            buttonLabel = "Completed";
          } else if (mode >= 1) {
            buttonLabel = `Resume (Phase ${mode}) ▶`;
          }
          return (
            <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px", // space between buttons
              whiteSpace: "nowrap",
            }}
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSurveyClick(row.original);
                }}
                size="sm"
                variant={isCompleted ? "secondary" : "primary"}
                disabled={isCompleted} // grey out if completed
              >
                {buttonLabel}
              </Button>
              {mode >= 1 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowResponses(row.original);
                  }}
               >
                  ☰
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  const initialState = {
    sortBy: [
      {
        id: "shuffled_case_number",
        desc: false,
      },
    ],
  };

  // read in case list
  const [caseList, setCaseList] = useState();
  const filteredCaseList = useMemo(() => {
    if (!caseList) return caseList;
    if (isAdmin) return caseList;
    return caseList.filter((c) => Number(c.case_id) <= 90);
  }, [caseList, isAdmin]);
  
  useEffect(() => {
    (async () => {
      try {
        if (!debug_mode) {
          // if in survey mode, fetch micro_ids from /ipc_case_list
          const caseRes = await api.get("/ipc_case_list");
          if (caseRes.ok) {
            setCaseList(caseRes.body);
            console.log("caseRes", caseRes.body);
          } else {
            setCaseList(null);
            console.error("Error fetching all case_list in survey mode");
          }
        } else {
          // if in debug mode, fetch all from case_list
          const caseRes = await api.get("/case_list");
          if (caseRes.ok) {
            setCaseList(caseRes.body);
          } else {
            setCaseList(null);
            console.error("Error fetching all case_list in debug mode");
          }
        }
      } catch (error) {
        console.error("Error in data fetching:", error);
        setCaseList(null);
      }
    })();
  }, [api, debug_mode]); 

  const handleShowResponses = async (caseRow) => {
    try {
      const res = await api.get(
        `/me/response?case_id=${caseRow.case_id}&participant_id=${user.id}`
      );
      if (res.ok) {
        setResponsesList(res.body); // store the array of user_response rows
        setShowResponsesModal(true);
      } else {
        console.error("Failed to fetch user_responses", res);
      }
    } catch (error) {
      console.error("Error fetching user_responses", error);
    }
  };

  return (
    <Body sidebar>
      <div className="all-patient-table-wrapper">
        <p>
          A list of <i>C. difficile</i> cases is shown below, click on any case to begin or resume the study. All names, MRNs and dates are fictional.
        </p>
        {caseList === undefined ? (
          <Spinner animation="border" />
        ) : (
          <>
            <CaseTable
              columns={debug_mode ? debugColumns : surveyColumns}
              data={filteredCaseList}
              initialState={initialState}
              surveyed={surveyed}
              onRowClick={handleRowClick}
            />
            <div className="post-study-reminder">
              <div>
                <div className="post-study-reminder-title">Complete the study 📋</div>
                <p className="post-study-reminder-text">
                  After completing your cases, please complete the post-study questionnaire to finish the study. (10 minutes)
                </p>
              </div>
              <NavLink to="/final" className="post-study-reminder-link">
                Post-study questionnaire
              </NavLink>
            </div>
            <div className="case-disclaimer">
              <div className="case-disclaimer-title">Ethics approval</div>
              <p className="case-disclaimer-text">
                Approved by the University of Oxford Medical Sciences Interdivisional Research Ethics Committee (Ref:
                R94653/RE001)
                <br></br>
                Full study title: &ldquo;Evaluation of an infection prevention and
                control (IPC) dashboard: How can hospital admission, ward movement and pathogen
                genomic data influence IPC decision-making in hospitals&rdquo; 
              </p>
              <div className="case-disclaimer-title">Funding and support</div>
              <p className="case-disclaimer-text">
                This project is funded by the NIHR Health Protection Research Unit in Healthcare
                Associated Infections and Antimicrobial Resistance at Oxford University in
                partnership with the UK Health Security Agency (UKHSA) (NIHR207397) and the NIHR
                Biomedical Research Centre, Oxford. <br></br>The views expressed are those of the authors
                and not necessarily those of the NHS, the NIHR, the Department of Health and Social
                Care or UKHSA.
              </p>
              <div className="case-disclaimer-title">Contact details</div>
              <p className="case-disclaimer-text">
                Any questions or concerns? Please contact {<a style={{color: "inherit"}} href="mailto:augustine.luk@ndm.ox.ac.uk">augustine.luk@ndm.ox.ac.uk</a>}
              </p>
              <div className="support-logos">
                <div className="support-logos-row">
                  <img
                    src={hpruLogo}
                    alt="NIHR Health Protection Research Unit in Healthcare Associated Infections and Antimicrobial Resistance"
                    className="support-logo primary-logo"
                  />
                  <img src={oxLogo} alt="University of Oxford" className="support-logo" />
                  <img src={bdiLogo} alt="Big Data Institute" className="support-logo" />
                  <img
                    src={mmmLogo}
                    alt="Modernising Medical Microbiology"
                    className="support-logo"
                  />
                </div>
              </div>
            </div>
              <p className="version-text">
                v0.1.0.0
                Copyright &copy; 2026 University of Oxford
              </p>
          </>
        )}
      </div>

      {/* Modal for retrieving and displaying responses */}
      <Modal show={showResponsesModal} onHide={() => setShowResponsesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Recorded Responses</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {responsesList.length === 0 ? (
            <p>No user responses found.</p>
          ) : (
            <>
              {responsesList.map((record, idx) => {
                return (
                  <div
                    key={record.id}
                    style={{ border: "1px solid #ccc", marginBottom: "16px", padding: "10px", borderRadius: "4px" }}
                  >
                    <h5>
                      <span style={{color: "darkgrey"}}>#{record.id}</span> Phase {record.phase}
                    </h5>
                    <p style={{ marginBottom: "4px" }}>
                      <strong>Participant ID:</strong>{" "}
                      {record.participant_id
                        ? record.participant_id
                        : "(none)"}
                      {"  |  "}
                      <strong>Case ID:</strong>{" "}
                      {record.case_id
                        ? record.case_id
                        : "(none)"}
                      {"  |  "}
                      {/* <strong>Case Order:</strong>{" "}
                      {record.case_order
                        ? record.case_order
                        : "(none)"}
                      {"  |  "} */}
                      <strong>Patient ID:</strong>{" "}
                      {record.patient_id
                        ? record.patient_id
                        : "(none)"}
                      {"  |  "}
                      <strong>Micro ID:</strong>{" "}
                      {record.micro_id
                        ? record.micro_id
                        : "(none)"}
                    </p>
                    <p style={{ marginBottom: "4px" }}>
                      <strong>Start:</strong>{" "}
                      {record.start_timestamp
                        ? new Date(record.start_timestamp).toLocaleString()
                        : "(none)"}
                      {"  |  "}
                      <strong>Submit:</strong>{" "}
                      {record.submit_timestamp
                        ? new Date(record.submit_timestamp).toLocaleString()
                        : "(none)"}
                    </p>

                    {/* Now a table for the question/response details */}
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th style={{ width: "5%" }}>QID</th>
                          <th style={{ width: "35%" }}>Question</th>
                          <th>Responses</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* record.response is an array of questions */}
                        {(record.response || []).map((questionObj, qIndex) => {
                          if (!questionObj) return null;
                          const questionText = questionObj.question_text || `Question ${questionObj.question_id}`;
                          
                          // Filter out null or false-value responses
                          const validResponses = (questionObj.responses || []).filter(
                            (r) => r && r.response_value
                          );

                          // create a small list or table of each response
                          // here we'll make bullet points
                          return (
                            <tr key={qIndex}>
                              <td>{questionObj.question_id}</td>
                              <td>{questionText}</td>
                              <td>
                                {validResponses.length === 0 ? (
                                  <em>No answer</em>
                                ) : (
                                  <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
                                    {validResponses.map((resp, respIdx) => {
                                      const confPart = resp.response_confidence
                                        ? ` [${resp.response_confidence}]`
                                        : "";
                                      
                                      // If Q6 or any question has a ward name:
                                      const wardPart = resp.response_ward_name
                                        ? `[${resp.response_ward_name}] `
                                        : "";
      
                                      return (
                                        <li key={respIdx}>
                                          {/* response_text plus any indicator */}
                                          <span style={{fontWeight: 600}}>{wardPart}</span>
                                          {resp.response_text}
                                          <span style={{color: "darkgrey"}}>{confPart}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResponsesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Body>
  );
}
